import React, { useMemo, useState, useRef, MouseEvent } from "react";
import { Parallax } from "react-scroll-parallax";
import { Link } from "react-router-dom";
import "../../scss/footer.scss";
import logoPL from "../../assets/CC_logo_XS_kolor_czarne.png";
import logoENG from "../../assets/CC-logo-poziom__ciemne_ENG.png";
import { SocialIcons } from "../../common/components/SocialIcons";
import { useI18n } from "../../i18n/I18nProvider";

const CDN24_LOGO_PL = process.env.PUBLIC_URL + "./cdn24info.png";
const CDN24_LOGO_ENG = process.env.PUBLIC_URL + "./cdn24.info ang.png";

type FooterSquare = {
  id: number;
  size: number;
  left: number;
  top: number;
  speed: number;
};

// Definicja danych dla ikonek społecznościowych

const FooterComponent: React.FC = () => {
  const { t, language } = useI18n();
  const logo = language === "pl" ? logoPL : logoENG;
  const cdn24Logo = language === "pl" ? CDN24_LOGO_PL : CDN24_LOGO_ENG;
  const year = new Date().getFullYear();
  const footerRef = useRef<HTMLElement>(null);
  const [mouseData, setMouseData] = useState({ x: -1000, y: -1000, w: 0, h: 0 });

  const squares = useMemo<FooterSquare[]>(() => {
    const arr: FooterSquare[] = [];
    for (let i = 0; i < 25; i++) {
      const size = 16 + Math.random() * 30; 
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const normalized = (size - 16) / 30;
      const speed = (Math.random() > 0.5 ? 1 : -1) * (5 + normalized * 15);
      arr.push({ id: i, size, left, top, speed });
    }
    return arr;
  }, []);

  const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
    if (!footerRef.current) return;
    const rect = footerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top; 

    setMouseData({ x, y, w: rect.width, h: rect.height });
    footerRef.current.style.setProperty('--mouse-x', `${x}px`);
    const glowOpacity = Math.max(0, 1 - (y / 150));
    footerRef.current.style.setProperty('--glow-opacity', `${glowOpacity}`);
  };

  const handleMouseLeave = () => {
    setMouseData({ x: -1000, y: -1000, w: 0, h: 0 });
    if (footerRef.current) {
      footerRef.current.style.setProperty('--glow-opacity', '0');
    }
  };

  return (
    <footer
      className="cyber-footer"
      ref={footerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="cyber-footer__bg">
        {squares.map((sq) => {
          let transX = 0;
          let transY = 0;

          if (mouseData.w > 0) {
            const sqPixelX = (sq.left / 100) * mouseData.w;
            const sqPixelY = (sq.top / 100) * mouseData.h;
            const dx = mouseData.x - sqPixelX;
            const dy = mouseData.y - sqPixelY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const radius = 300;

            if (dist < radius) {
              const pull = Math.pow((radius - dist) / radius, 2);
              const sizeFactor = (sq.size - 16) / 30;
              const strength = 0.015 + (sizeFactor * 0.05);
              transX = dx * pull * strength;
              transY = dy * pull * strength;
            }
          }

          return (
            <Parallax
              key={sq.id}
              speed={sq.speed}
              className="cyber-footer__square-wrapper"
              style={{ left: `${sq.left}%`, top: `${sq.top}%` }}
            >
              <div
                className="cyber-footer__square"
                style={{
                  width: sq.size,
                  height: sq.size,
                  transform: `translate(${transX}px, ${transY}px)`,
                }}
              />
            </Parallax>
          );
        })}
      </div>

      <div className="cyber-footer__inner">
        <div className="cyber-footer__content">
          <div className="cyber-footer__logos">
            <Link to="/" className="cyber-footer__logo-link">
              <img
                src={logo}
                alt={t("footer_logo_alt")}
                className="cyber-footer__logo"
              />
            </Link>
            <a
              href="https://cdn24.info"
              target="_blank"
              rel="noopener noreferrer"
              className="cyber-footer__logo-link"
              aria-label={t("footer_cdn24_label")}
            >
              <img
                src={cdn24Logo}
                alt={t("footer_cdn24_alt")}
                className="cyber-footer__logo cyber-footer__logo--cdn24"
              />
            </a>
          </div>
          <p className="cyber-footer__description">
            {t("footer_description")}
          </p>

          <div className="cyber-footer__socials-main">
            <span className="cyber-footer__socials-label">{t("footer_follow_us")}</span>
            {/* WYWOŁANIE KOMPONENTU I PRZEKAZANIE DANYCH */}
            <SocialIcons/>
          </div>
        </div>

        <div className="cyber-footer__bottom">
          <div className="cyber-footer__bottom-left">
            <span className="cyber-footer__bottom-year">© {year}</span>
            <span className="cyber-footer__bottom-text">
              {t("footer_copyright")}
            </span>
          </div>

          <div className="cyber-footer__bottom-right">
            <div className="cyber-footer__bottom-links">
              <Link to="/projekty/aktualne" className="cyber-footer__bottom-link">{t("footer_link_projects")}</Link>
              <Link to="/kontakt" className="cyber-footer__bottom-link">{t("footer_link_contact")}</Link>
              <Link to="/biznes" className="cyber-footer__bottom-link">{t("footer_link_business")}</Link>
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default FooterComponent;